import React from 'react';
import { GripVertical } from "lucide-react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';

import {
    getSortedColumnEntries,
    resolveItemListColumnConfig,
    updateItemListColumnOrder,
    updateItemListColumnVisibility,
    ItemListColumnDefinition,
    ItemListColumnPreferences,
} from '../model/item-list.types';

interface SortableRowProps {
    colId: string;
    column: ItemListColumnDefinition;
    onVisibleChange: (colId: string, visible: boolean) => void;
}

interface ItemListDragEndEvent {
    active: { id: string | number };
    over: { id: string | number } | null;
}

const SortableRow: React.FC<SortableRowProps> = ({ colId, column, onVisibleChange }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: colId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="text-center" {...listeners} {...attributes} style={{ cursor: 'grab' }}>
                <span className="text-muted" title="드래그하여 순서 변경">
                    <GripVertical className="drag-icon" aria-hidden="true" />
                </span>
            </td>
            <td>{column.order}</td>
            <td>{column.label}</td>
            <td>{column.labelShort}</td>
            <td>
                <div className="form-check form-switch m-0">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id={`visible-${column.id}`}
                        checked={column.visible}
                        onChange={(e) => onVisibleChange(colId, e.target.checked)}
                    />
                </div>
            </td>
        </tr>
    );
};

interface ItemListColumnConfigTableProps {
    preferences: ItemListColumnPreferences;
    onPreferencesChange: (newPreferences: ItemListColumnPreferences) => void;
}

const ItemListColumnConfigTable: React.FC<ItemListColumnConfigTableProps> = ({
    preferences,
    onPreferencesChange,
}) => {
    const config = React.useMemo(
        () => resolveItemListColumnConfig(preferences),
        [preferences],
    );

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: ItemListDragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const entries = getSortedColumnEntries(config);
            const oldIndex = entries.findIndex(([colId]) => colId === active.id);
            const newIndex = entries.findIndex(([colId]) => colId === over.id);
            const reorderedEntries = arrayMove(
                Array.from(entries) as [string, ItemListColumnDefinition][],
                oldIndex,
                newIndex,
            ) as [string, ItemListColumnDefinition][];

            onPreferencesChange(updateItemListColumnOrder(
                preferences,
                reorderedEntries.map(([colId]) => colId),
            ));
        }
    };

    const handleVisibleChange = (colId: string, visible: boolean) => {
        onPreferencesChange(updateItemListColumnVisibility(
            preferences,
            colId,
            visible,
        ));
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th className="text-start" style={{ width: '40px' }}>이동</th>
                            <th className="text-start" style={{ width: '80px' }}>번호</th>
                            <th className="text-start">컬럼명</th>
                            <th className="text-start">숏네임</th>
                            <th className="text-start" style={{ width: '120px' }}>표시 여부</th>
                        </tr>
                    </thead>
                    <tbody>
                        <SortableContext
                            items={getSortedColumnEntries(config).map(([key]) => key)}
                            strategy={verticalListSortingStrategy}
                        >
                            {getSortedColumnEntries(config).map(([colId, column]) => (
                                <SortableRow
                                    key={colId}
                                    colId={colId}
                                    column={column}
                                    onVisibleChange={handleVisibleChange}
                                />
                            ))}
                        </SortableContext>
                    </tbody>
                </table>
            </div>
        </DndContext>
    );
};

export default ItemListColumnConfigTable;
